import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  House, CalendarBlank, ClockClockwise, CheckCircle, Buildings, Users,
  ChartBar, Gear, SignOut, List, CalendarPlus, WarningCircle, Wallet, X,
  ArrowsClockwise, ClockCounterClockwise,
} from '@phosphor-icons/react';

const LOGO_URL = '/logo-s2e.png';

const Sidebar = ({ mobileOpen, onClose }) => {
  const { user, logout, isAdmin, isManager, isTeamLeader } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Close sidebar on navigation (mobile)
  useEffect(() => { if (mobileOpen) onClose(); }, [location.pathname]);

  const handleLogout = async () => {
    onClose();
    await logout();
    navigate('/login');
  };

  const navItems = [
    { to: '/', icon: House, label: 'Dashboard', show: true },
    { to: '/calendar', icon: CalendarBlank, label: 'Calendario', show: true },
    { to: '/schedules', icon: CalendarPlus, label: 'Gestione Turni', show: isTeamLeader },
    { to: '/my-requests', icon: ClockClockwise, label: 'Le Mie Richieste', show: true },
    { to: '/approvals', icon: CheckCircle, label: 'Approvazioni', show: isTeamLeader },
    { to: '/anomalies', icon: WarningCircle, label: 'Segnalazioni', show: isTeamLeader },
    { to: '/employee-history', icon: ClockCounterClockwise, label: 'Storico', show: true },
    { to: '/organization', icon: Buildings, label: 'Organizzazione', show: isManager },
    { to: '/users', icon: Users, label: 'Utenti', show: isAdmin },
    { to: '/balances', icon: Wallet, label: 'Gestione Saldi', show: isManager },
    { to: '/reports', icon: ChartBar, label: 'Report', show: isTeamLeader },
    { to: '/activity-logs', icon: List, label: 'Log Attività', show: isManager },
    { to: '/settings', icon: Gear, label: 'Impostazioni', show: isAdmin },
  ];

  const getInitials = (name) => name?.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2) || 'U';
  const getRoleBadge = (role) => {
    const badges = {
      super_admin: { label: 'ADMIN', class: 'bg-[#002FA7] text-white' },
      manager: { label: 'MANAGER', class: 'bg-[#0A0A0A] text-white' },
      team_leader: { label: 'TEAM LEAD', class: 'bg-[#52525B] text-white' },
      employee: { label: 'DIPENDENTE', class: 'bg-[#E4E4E7] text-[#0A0A0A]' },
    };
    return badges[role] || badges.employee;
  };

  return (
    <>
      {/* Backdrop (mobile only) */}
      {mobileOpen && (
        <div className="fixed inset-0 bg-black/40 z-40 lg:hidden" onClick={onClose} />
      )}

      <aside className={`
        w-64 border-r border-[#E4E4E7] bg-[#FAFAFA] flex flex-col h-screen fixed left-0 top-0 z-50
        transition-transform duration-200 ease-in-out
        ${mobileOpen ? 'translate-x-0' : '-translate-x-full'}
        lg:translate-x-0
      `}>
        <div className="border-b border-[#E4E4E7] flex items-center justify-between px-6 py-4">
          <div className="flex flex-col items-start">
            <img src={LOGO_URL} alt="DCO Planner" className="h-7 w-auto object-contain" data-testid="app-logo" />
            <div className="flex justify-between w-full mt-0.5">
              {'DCO PLANNER'.split('').map((ch, i) => (
                <span key={i} className="font-heading text-[9px] font-bold text-[#0A0A0A]">{ch === ' ' ? '\u00A0' : ch}</span>
              ))}
            </div>
          </div>
          <button onClick={onClose} className="lg:hidden w-8 h-8 flex items-center justify-center" data-testid="close-sidebar-btn">
            <X size={20} weight="bold" />
          </button>
        </div>

        <nav className="flex-1 py-4 px-3 overflow-y-auto">
          <ul className="space-y-0.5">
            {navItems.filter((item) => item.show).map((item) => (
              <li key={item.to}>
                <NavLink to={item.to} end={item.to === '/'}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2.5 text-sm font-medium transition-colors duration-150 ${
                      isActive ? 'bg-[#0A0A0A] text-white' : 'text-[#52525B] hover:bg-[#E4E4E7] hover:text-[#0A0A0A]'
                    }`
                  }
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}>
                  <item.icon size={20} weight="bold" />
                  {item.label}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>

        <div className="border-t border-[#E4E4E7] p-4">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 bg-[#0A0A0A] flex items-center justify-center text-white font-bold text-xs flex-shrink-0">
              {getInitials(user?.name)}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-[#0A0A0A] truncate">{user?.name}</p>
              <span className={`inline-block px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${getRoleBadge(user?.role).class}`}>
                {getRoleBadge(user?.role).label}
              </span>
            </div>
          </div>
          <button onClick={handleLogout}
            className="w-full flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-[#FF2A00] border border-[#FF2A00] hover:bg-[#FF2A00] hover:text-white transition-colors"
            data-testid="logout-btn">
            <SignOut size={18} weight="bold" /> Esci
          </button>
        </div>
      </aside>
    </>
  );
};

// Mobile top bar component
export const MobileTopBar = ({ onMenuOpen }) => {
  return (
    <div className="lg:hidden fixed top-0 left-0 right-0 h-14 bg-white border-b border-[#E4E4E7] flex items-center justify-between px-4 z-30">
      <button onClick={onMenuOpen} className="w-10 h-10 flex items-center justify-center" data-testid="open-sidebar-btn">
        <List size={24} weight="bold" />
      </button>
      <div className="flex items-center gap-2">
        <img src={LOGO_URL} alt="DCO Planner" className="h-5 w-auto object-contain" />
        <span className="font-heading text-xs font-bold tracking-wider">DCO PLANNER</span>
      </div>
      <NotificationBellInline />
    </div>
  );
};

// Inline notification bell (used in both mobile and desktop bars)
const NotificationBellInline = () => {
  const NotificationBell = React.lazy(() => import('./NotificationBell'));
  return (
    <React.Suspense fallback={<div className="w-10 h-10" />}>
      <NotificationBell />
    </React.Suspense>
  );
};

// Desktop top bar (sits above content area on large screens)
export const DesktopTopBar = () => {
  return (
    <div className="hidden lg:flex h-12 border-b border-[#E4E4E7] items-center justify-end px-6 bg-white">
      <NotificationBellInline />
    </div>
  );
};

export default Sidebar;
